import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanRelatedSeminarDetail,
} from '../app/yowanContentClient';
import {
  getStakeholderApplicationsHref,
  getStakeholderMessagesHref,
  StakeholderGuardNotice,
  StakeholderShell,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
import {
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const ReadOnlyField = ({ label, value, multiline = false, rows = 3 }) => {
  const safeValue = value || '-';

  return (
    <label className={`c-form__field yowan-admin-readonly-field${multiline ? ' yowan-admin-readonly-field--wide' : ''}`}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea className="c-form__control c-form__control--textarea" readOnly rows={rows} value={safeValue} />
      ) : (
        <input className="c-form__control" readOnly type="text" value={safeValue} />
      )}
    </label>
  );
};

const StatusBadge = ({ label, tone = 'draft' }) => (
  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${tone}`}>
    {label}
  </span>
);

const DetailSection = ({ title, lead, children }) => (
  <section className="yowan-admin-detail-panel yowan-admin-event-detail__section">
    <header className="yowan-admin-event-detail__section-header">
      <h2 className="yowan-admin-detail-panel__title">{title}</h2>
      {lead ? <p className="yowan-admin-detail-panel__lead">{lead}</p> : null}
    </header>
    {children}
  </section>
);

const resolveStakeholderSeminarApplicationsCount = (record = {}) => {
  const explicitCount = Number(record.applicationsCount || 0);
  const slotCount = (record.slots || []).reduce((total, slot) => {
    if (!slot?.hasApplicationsCount) {
      return total;
    }

    return total + Number(slot.applicationsCount || 0);
  }, 0);

  return Math.max(explicitCount, slotCount);
};

const hasStakeholderSlotApplicationsCount = (slot = {}) => (
  slot.hasApplicationsCount === true ||
  slot.applicationsCount !== undefined ||
  slot.appliedCount !== undefined
);

const resolveStakeholderSlotApplicationsCount = (slot = {}) => Number(
  slot.applicationsCount ?? slot.appliedCount ?? 0,
);

const StakeholderSeminarDetailPage = ({ guardOptions }) => {
  const { eventId } = useParams();
  const content = yowanPageContent['/stakeholder/seminars/:eventId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanRelatedSeminarDetail(eventId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError(content?.loadErrorMessage || '担当セミナー詳細を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, eventId, guard.status]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  return (
    <StakeholderShell activeNavHref="/stakeholder/seminars" routeKey="/stakeholder/seminars/:eventId">
      <AdminPageHeader
        title={record ? `${record.title} 詳細` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="applications" to={getStakeholderApplicationsHref(eventId, preservedDomainSearch)}>
            申込一覧
          </Link>,
          <Link key="messages" to={getStakeholderMessagesHref(eventId, preservedDomainSearch)}>
            申込メッセージ
          </Link>,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="担当セミナー詳細を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError && record ? (
          <div className="yowan-admin-event-detail">
            <DetailSection title="基本情報">
              <div className="yowan-admin-event-detail__status-row">
                <StatusBadge label={record.isPublished ? '公開中' : '非公開'} tone={record.isPublished ? 'published' : 'draft'} />
                <StatusBadge label={record.isActive ? '有効' : '停止中'} tone={record.isActive ? 'published' : 'draft'} />
              </div>
              <div className="yowan-admin-event-detail__grid">
                <ReadOnlyField label="セミナー名" value={record.title} />
                <ReadOnlyField label="サブタイトル" value={record.subtitle} />
                <ReadOnlyField label="主催者" value={record.organizerName} />
                <ReadOnlyField label="参加料金" value={record.fullParticipationFeeLabel} />
                <ReadOnlyField label="申込者数" value={`${resolveStakeholderSeminarApplicationsCount(record)}名`} />
                <ReadOnlyField label="概要" value={record.summary} multiline rows={3} />
                <ReadOnlyField label="案内文" value={record.guidance} multiline rows={5} />
              </div>
            </DetailSection>

            <DetailSection title="開催コマ">
              {record.slots?.length ? (
                <div className="yowan-stakeholder-slot-list">
                  {record.slots.map((slot) => (
                    <article key={slot.id || `${slot.datetimeLabel}-${slot.locationLabel}`} className="yowan-stakeholder-slot">
                      <h3>{slot.title || slot.datetimeLabel || '開催コマ'}</h3>
                      <dl>
                        <div><dt>日時</dt><dd>{slot.datetimeLabel || '-'}</dd></div>
                        <div><dt>会場</dt><dd>{slot.locationLabel || slot.roomName || '-'}</dd></div>
                        <div><dt>講師</dt><dd>{slot.instructorName || '-'}</dd></div>
                        <div><dt>参加費</dt><dd>{slot.feeLabel || '-'}</dd></div>
                        <div><dt>持ち物</dt><dd>{slot.thingsToBring || '-'}</dd></div>
                        <div>
                          <dt>申込</dt>
                          <dd>
                            {hasStakeholderSlotApplicationsCount(slot)
                              ? `${resolveStakeholderSlotApplicationsCount(slot)}名`
                              : '-'}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="yowan-admin-form__message">開催コマは登録されていません。</p>
              )}
            </DetailSection>
          </div>
        ) : null}
      </div>
    </StakeholderShell>
  );
};

export default StakeholderSeminarDetailPage;
